/**
 * ui/services/complianceService.ts
 *
 * v6.5 patch goals:
 * - 修正 export 缺失（refreshSeedCards）
 * - 修正 Firestore schema 與 UI 的 IComplianceCard 對齊
 * - Seed CSV -> 轉成專案管控卡片（寫入 compliance_cards）
 * - 提供最小相容：舊 signature listComplianceCards(type, includeHistory)
 */

import {
  db,
  functions,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "../../firebaseClient";

import type {
  ComplianceCard as IComplianceCard,
  ComplianceCardType as CardType,
  ComplianceCardStatus as CardStatus,
  ComplianceRiskLevel as RiskLevel,
  CrawlerRunLog,
} from "../types/compliance";

import type { AttachmentMeta } from "../types/attachments";
import type { ApprovalModule } from "../types/approval";
import { createOrResetApprovalFlow } from "./approvalFlowService";

// ui/constants/departments.ts defines the actual codes; we keep it flexible here.
type DeptCode = string;

// ----------------------------
// Constants & helpers
// ----------------------------

const CARDS_COLLECTION = "compliance_cards";
const RUNS_COLLECTION = "crawlerRuns";

const HISTORY_STATUSES: CardStatus[] = ["ACK_ARCHIVED", "COMPLETED"];

// Helper for UI bucketing (e.g., "歷史彙編" tab).
export function isComplianceHistoryStatus(status?: string | null): boolean {
  const s = (status || "").toUpperCase();
  return HISTORY_STATUSES.includes(s as CardStatus);
}

function normalizeType(raw: any): CardType {
  const t = String(raw ?? "").toLowerCase();
  if (t === "intel") return "intel";
  if (t === "project") return "project";
  // legacy field
  const k = String(raw?.kind ?? "").toLowerCase();
  if (k === "seed" || k === "project") return "project";
  return "intel";
}

function normalizeStatus(raw: any): CardStatus {
  const s = String(raw ?? "").toUpperCase();

  // Align with ui/types/compliance.ts (ComplianceCardStatus)
  const map: Record<string, CardStatus> = {
    DRAFT: "DRAFT",
    IN_REVIEW: "IN_REVIEW",
    APPROVED: "APPROVED",
    REJECTED: "REJECTED",
    RETURNED: "RETURNED",
    NOT_STARTED: "NOT_STARTED",
    COMPLETED: "COMPLETED",
    ACK_ARCHIVED: "ACK_ARCHIVED",
    MOVED_TO_PROJECT: "MOVED_TO_PROJECT",
    // legacy
    ARCHIVED: "ACK_ARCHIVED",
    PENDING: "IN_REVIEW",
    IN_PROGRESS: "IN_REVIEW",
  };

  return map[s] ?? "NOT_STARTED";
}

function normalizeRiskLevel(raw: any): RiskLevel {
  const s = String(raw ?? "").toUpperCase();
  if (s === "HIGH") return "HIGH";
  if (s === "MEDIUM") return "MEDIUM";
  if (s === "LOW") return "LOW";
  return "MEDIUM";
}

function safeString(v: any, fallback = ""): string {
  return typeof v === "string" ? v : v == null ? fallback : String(v);
}

function clampInt(n: any, fallback: number): number {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.round(x);
}

function mapOwnerDept(raw: string): DeptCode {
  const v = safeString(raw).trim();
  const u = v.toUpperCase();

  // Accept both dept codes (GM/FA/AP/IT/LG/SL1/SL2/LI/PT/QC/MK/SYS) and Chinese hints.
  const direct = ["GM", "FA", "AP", "IT", "LG", "SL1", "SL2", "LI", "PT", "QC", "MK", "SYS"] as const;
  if ((direct as readonly string[]).includes(u)) return u as DeptCode;

  if (/[法務]/.test(v)) return "LG";
  if (/(行銷|市場|廣告)/.test(v)) return "MK";
  if (/(資訊|IT|資安|系統)/i.test(v)) return "IT";
  if (/(財務|會計|稅務|發票)/.test(v)) return "FA";
  if (/(人資|行政|HR)/i.test(v)) return "AP";
  if (/(總經理)/.test(v)) return "GM";
  if (/(業務一)/.test(v)) return "SL1";
  if (/(業務二)/.test(v)) return "SL2";
  if (/(物流|倉儲|出貨|配送)/.test(v)) return "LI";
  if (/(採購)/.test(v)) return "PT";
  if (/(品保|品管)/.test(v)) return "QC";
  if (/(系統管理|管理者)/.test(v)) return "SYS";

  return "LG";
}

// ----------------------------
// CSV parsing (minimal, robust enough)
// ----------------------------

function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    // ignore completely empty row
    const anyVal = row.some((c) => c.trim() !== "");
    if (anyVal) rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        const next = text[i + 1];
        if (next === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        pushField();
      } else if (ch === "\n") {
        pushField();
        pushRow();
      } else if (ch === "\r") {
        // ignore (handle \r\n)
      } else {
        field += ch;
      }
    }
  }
  // last field/row
  pushField();
  pushRow();

  if (rows.length === 0) return [];

  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] ?? "").trim();
    });
    return obj;
  });
}

function safeSnapExists(snap: any): boolean {
  if (!snap) return false;
  const v = (snap as any).exists;
  try {
    return typeof v === "function" ? v.call(snap) : Boolean(v);
  } catch {
    // fallback
    return Boolean(v);
  }
}

function filenameFromUrl(url: string, fallback = "attachment.pdf"): string {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").filter(Boolean).pop();
    if (!last) return fallback;
    // If it doesn't look like a file, still return something stable
    return decodeURIComponent(last) || fallback;
  } catch {
    const last = url.split("/").filter(Boolean).pop();
    return last || fallback;
  }
}

function toAttachmentMetaArray(links: any): AttachmentMeta[] {
  const arr = Array.isArray(links) ? links : [];
  const items = arr
    .map((x) => {
      const url = safeString(typeof x === "string" ? x : (x as any)?.url);
      if (!url) return null;
      const name = safeString(typeof x === "string" ? "" : (x as any)?.name);
      return { url, name: name || filenameFromUrl(url) };
    })
    .filter(Boolean) as Array<{ url: string; name: string }>;

  return items.map((it) => ({ name: it.name, url: it.url }));
}

async function sha256Hex(input: string): Promise<string> {
  // prefer WebCrypto
  const g: any = globalThis as any;
  const subtle = g?.crypto?.subtle;
  if (subtle?.digest) {
    const enc = new TextEncoder();
    const buf = await subtle.digest("SHA-256", enc.encode(input));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // fallback (non-crypto hash)
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = (h * 33) ^ input.charCodeAt(i);
  return (h >>> 0).toString(16).padStart(8, "0");
}

// ----------------------------
// Normalization: Firestore -> IComplianceCard
// ----------------------------

function normalizeDocToCard(id: string, raw: any): IComplianceCard {
  const type = normalizeType(raw?.type ?? raw);
  const status = normalizeStatus(raw?.status);

  const agencyName = safeString(raw?.agencyName ?? raw?.authorityName ?? raw?.sourceName ?? raw?.agency ?? "");
  const agencyId = safeString(raw?.agencyId ?? raw?.authorityId ?? raw?.sourceId ?? raw?.agencyId ?? "");

  // sectionA
  const dateText = safeString(
    raw?.sectionA?.dateText ??
      raw?.dateText ??
      raw?.sectionA?.announcementDate ??
      raw?.announcementDate ??
      raw?.publishedAt ??
      ""
  );
  const date = safeString(raw?.sectionA?.date ?? raw?.date ?? dateText);

  // attachmentLinks can come from either attachmentLinks (string[]) or attachments (AttachmentMeta[])
  const rawLinks =
    raw?.sectionA?.attachmentLinks ??
    raw?.attachmentLinks ??
    (Array.isArray(raw?.sectionA?.attachments) ? raw.sectionA.attachments.map((a: any) => a?.url) : []);

  const attachmentLinks = Array.isArray(rawLinks)
    ? rawLinks.map((x: any) => safeString(typeof x === "string" ? x : x?.url)).filter(Boolean)
    : [];

  const sectionA = {
    title: safeString(raw?.sectionA?.title ?? raw?.title ?? ""),
    subject: safeString(raw?.sectionA?.subject ?? raw?.subject ?? raw?.title ?? ""),
    date: date ? date : null,
    dateText,
    summary: safeString(raw?.sectionA?.summary ?? raw?.summary ?? ""),
    sourceUrl: safeString(raw?.sectionA?.sourceUrl ?? raw?.sourceUrl ?? ""),
    announcementUrl: safeString(raw?.sectionA?.announcementUrl ?? raw?.announcementUrl ?? ""),
    attachmentLinks,
    legalBasis: Array.isArray(raw?.sectionA?.legalBasis ?? raw?.legalBasis)
      ? (raw?.sectionA?.legalBasis ?? raw?.legalBasis).map((x: any) => safeString(x)).filter(Boolean)
      : [],
    tags: raw?.sectionA?.tags ?? raw?.tags ?? [],
  };

  const sectionB = {
    ownerDept: mapOwnerDept(safeString(raw?.sectionB?.ownerDept ?? raw?.ownerDept ?? "LG")),
    ownerDeptSuggestionText: safeString(raw?.sectionB?.ownerDeptSuggestionText ?? raw?.ownerDeptSuggestionText ?? ""),
    content: safeString(raw?.sectionB?.content ?? raw?.content ?? ""),
    attachments: Array.isArray(raw?.sectionB?.attachments)
      ? raw.sectionB.attachments
      : [],
    topicTags: Array.isArray(raw?.sectionB?.topicTags)
      ? raw.sectionB.topicTags
      : [],
  };

  const createdAt = raw?.createdAt ?? null;
  const updatedAt = raw?.updatedAt ?? null;
  const archivedAt = raw?.archivedAt ?? null;

  const riskScore = clampInt(raw?.riskScore, 50);
  const riskLevel = normalizeRiskLevel(raw?.riskLevel);

  return {
    id,
    type,
    status,
    agencyName,
    agencyId,
    ownerDept: sectionB.ownerDept,
    riskScore,
    riskLevel,
    // minimal sections
    sectionA: {
      ...sectionA,
      attachments: Array.isArray(raw?.sectionA?.attachments)
        ? toAttachmentMetaArray(
            raw.sectionA.attachments.map((a: any) => ({
              name: safeString(a?.name) || filenameFromUrl(safeString(a?.url)),
              url: safeString(a?.url),
            }))
          )
        : toAttachmentMetaArray(attachmentLinks),
      fetchedAt: raw?.sectionA?.fetchedAt ?? null,
    },
    sectionB,
    coAssistSections: Array.isArray(raw?.coAssistSections) ? raw.coAssistSections : [],
    createdAt,
    updatedAt,
    archivedAt,
  };
}

// ----------------------------
// Public API
// ----------------------------

export async function getLatestCrawlerRun(): Promise<CrawlerRunLog | null> {
  const q = query(
    collection(db, RUNS_COLLECTION),
    orderBy("startedAt", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0].data() as any;

  // The UI expects these fields (ui/types/compliance.ts). We keep fallbacks for older run logs.
  return {
    id: snap.docs[0].id,
    startedAt: d.startedAt ?? null,
    finishedAt: d.finishedAt ?? d.endedAt ?? null,
    status: d.status ?? "SUCCESS",
    agenciesCount: d.agenciesCount ?? 0,
    fetchedCount: d.fetchedCount ?? d.processed ?? 0,
    createdCardsCount: d.createdCardsCount ?? d.createdCount ?? 0,
    dedupSkippedCount: d.dedupSkippedCount ?? 0,
    errorMessage: d.errorMessage,
  };
}

// --- listComplianceCards ---
// Overload signatures
export async function listComplianceCards(type?: string, includeHistory = false): Promise<IComplianceCard[]> {
  // 🔒 Index-less strategy:
  // Firestore will require a composite index when you combine where(type==...) + orderBy(updatedAt).
  // To keep the demo frictionless (no manual index creation), we:
  // 1) query by type WITHOUT orderBy
  // 2) sort by updatedAt/createdAt on the client
  const lim = type === "project" ? 300 : 200;

  const colRef = collection(db, CARDS_COLLECTION);
  const qRef = type
    ? query(colRef, where("type", "==", type), limit(lim))
    : query(colRef, limit(lim));

  const snap = await getDocs(qRef);

  let list = snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      ...data
    } as IComplianceCard;
  });

  // History filter (client-side)
  if (!includeHistory) {
    list = list.filter((c) => !isComplianceHistoryStatus(c.status));
  }

  // Client-side sort by updatedAt desc (fallback to createdAt)
  const toMillis = (ts: any): number => {
    if (!ts) return 0;
    if (typeof ts === "number") return ts;
    if (typeof ts === "string") {
      const n = Date.parse(ts);
      return Number.isFinite(n) ? n : 0;
    }
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.seconds === "number") {
      const ns = typeof ts?.nanoseconds === "number" ? ts.nanoseconds : 0;
      return ts.seconds * 1000 + Math.floor(ns / 1e6);
    }
    return 0;
  };

  list.sort((a, b) => {
    const am = toMillis((a as any).updatedAt) || toMillis((a as any).createdAt);
    const bm = toMillis((b as any).updatedAt) || toMillis((b as any).createdAt);
    return bm - am;
  });

  return list;
}

// --- Create / Update / Delete ---

export async function createComplianceCard(input: any): Promise<string> {
  // compat firestore: generate doc id via collection(...).doc()
  const ref = collection(db, CARDS_COLLECTION).doc();
  const now = serverTimestamp();

  const title = safeString(input?.title ?? input?.sectionA?.title, "新專案 (請填寫)");
  const summary = safeString(input?.summary ?? input?.sectionA?.summary, "");

  const ownerDept: DeptCode = safeString(input?.ownerDept ?? input?.sectionB?.ownerDept, "LG");
  const status: CardStatus = normalizeStatus(input?.status ?? "DRAFT");
  const riskLevel: RiskLevel = normalizeRiskLevel(input?.riskLevel ?? "MEDIUM");
  const riskScore = clampInt(input?.riskScore, 50);

  const sectionAIn = input?.sectionA ?? {};
  const sectionBIn = input?.sectionB ?? {};

  const attachmentLinks = Array.isArray(sectionAIn?.attachmentLinks)
    ? sectionAIn.attachmentLinks
    : splitLinks(sectionAIn?.attachmentLinks ?? "");

  const card: Omit<IComplianceCard, "id"> & Record<string, any> = {
    type: normalizeType(input?.type ?? "project"),
    status,
    agencyName: safeString(input?.agencyName ?? input?.agencyId, ""),
    agencyId: safeString(input?.agencyId, ""),
    ownerDept,
    riskScore,
    riskLevel,
    source: safeString(input?.source, "manual"),
    seedKey: safeString(input?.seedKey, ""),
    sectionA: {
      title,
      date: safeString(sectionAIn?.date ?? sectionAIn?.dateText ?? "") || null,
      announcementDate: sectionAIn?.announcementDate ?? null,
      dateText: safeString(sectionAIn?.dateText, ""),
      subject: safeString(sectionAIn?.subject, title),
      summary,
      sourceUrl: safeString(sectionAIn?.sourceUrl, ""),
      announcementUrl: safeString(sectionAIn?.announcementUrl, ""),
      attachmentLinks,
      legalBasis: Array.isArray(sectionAIn?.legalBasis) ? sectionAIn.legalBasis : [],
      attachments: Array.isArray(sectionAIn?.attachments)
        ? toAttachmentMetaArray(sectionAIn.attachments)
        : toAttachmentMetaArray(attachmentLinks),
      fetchedAt: sectionAIn?.fetchedAt ?? null,
    },
    sectionB: {
      ownerDept: safeString(sectionBIn?.ownerDept, ownerDept),
      ownerDeptSuggestionText: safeString(sectionBIn?.ownerDeptSuggestionText, ""),
      content: safeString(sectionBIn?.content, ""),
      attachments: Array.isArray(sectionBIn?.attachments) ? sectionBIn.attachments : [],
      topicTags: Array.isArray(sectionBIn?.topicTags) ? sectionBIn.topicTags : [],
    },
    coAssistSections: Array.isArray(input?.coAssistSections) ? input.coAssistSections : [],
    approvalFlowId: safeString(input?.approvalFlowId, ""),
    approvalHistory: Array.isArray(input?.approvalHistory) ? input.approvalHistory : [],
    rejectionLog: Array.isArray(input?.rejectionLog) ? input.rejectionLog : [],
    createdAt: null,
    updatedAt: null,
    archivedAt: null,
    createdBy: safeString(input?.createdBy, "unknown"),
  };

  await setDoc(ref, { ...card, createdAt: now, updatedAt: now });
  return ref.id;
}

export async function updateComplianceCard(
  id: string,
  patch: Partial<IComplianceCard>
): Promise<void> {
  const ref = doc(db, CARDS_COLLECTION, id);
  await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}


// ----------------------------
// Approval / status transitions (required by ui/components/compliance/ComplianceCard.tsx)
// ----------------------------

function getUserUid(user: any): string {
  return String(user?.id ?? user?.uid ?? user?.email ?? "unknown");
}

function getUserName(user: any): string {
  return String(user?.displayName ?? user?.name ?? user?.email ?? "unknown");
}

function normalizeComplianceCardForUI(id: string, input: any): IComplianceCard {
  const type: CardType = normalizeType(input?.type ?? input?.kind);
  const status: CardStatus = normalizeStatus(input?.status);
  const riskLevel: RiskLevel = normalizeRiskLevel(input?.riskLevel);

  const agencyName = safeString(input?.agencyName ?? input?.authority ?? input?.agency ?? "");
  const agencyId = safeString(input?.agencyId ?? input?.authorityId ?? "");
  const ownerDeptTop: DeptCode = safeString(input?.ownerDept ?? input?.sectionB?.ownerDept ?? "LG");

  const sectionAIn = input?.sectionA ?? {};
  const sectionBIn = input?.sectionB ?? {};

  const title = safeString(sectionAIn?.title ?? input?.title ?? input?.subject ?? "");
  const dateText = safeString(sectionAIn?.dateText ?? input?.dateText ?? "");
  const dateStr = safeString(sectionAIn?.date ?? "", "");
  const date: string | null = dateStr ? dateStr : dateText ? dateText : null;

  const summary = safeString(sectionAIn?.summary ?? input?.summary ?? "");
  const subject = safeString(sectionAIn?.subject ?? input?.subject ?? title, title);

  const sourceUrl = safeString(sectionAIn?.sourceUrl ?? input?.sourceUrl ?? input?.source ?? "");
  const announcementUrl = safeString(sectionAIn?.announcementUrl ?? input?.announcementUrl ?? input?.url ?? "");

  const attachmentLinks = Array.isArray(sectionAIn?.attachmentLinks)
    ? sectionAIn.attachmentLinks
    : splitLinks(
        sectionAIn?.attachmentLinks ??
          input?.attachmentLinks ??
          input?.attachmentLink ??
          input?.attachmentUrl ??
          ""
      );

  const attachments = Array.isArray(sectionAIn?.attachments)
    ? toAttachmentMetaArray(sectionAIn.attachments)
    : toAttachmentMetaArray(attachmentLinks);

  const sectionA: IComplianceCard["sectionA"] = {
    title,
    date,
    announcementDate: sectionAIn?.announcementDate ?? input?.announcementDate ?? null,
    dateText: dateText || (date ? String(date) : ""),
    subject,
    summary,
    sourceUrl,
    announcementUrl,
    attachmentLinks,
    legalBasis: Array.isArray(sectionAIn?.legalBasis) ? sectionAIn.legalBasis : [],
    attachments,
    fetchedAt: sectionAIn?.fetchedAt ?? input?.fetchedAt ?? null,
  };

  const sectionB: IComplianceCard["sectionB"] = {
    ownerDept: safeString(sectionBIn?.ownerDept, ownerDeptTop),
    ownerDeptSuggestionText: safeString(
      sectionBIn?.ownerDeptSuggestionText ?? input?.ownerDeptSuggestionText,
      ""
    ),
    topicTags: Array.isArray(sectionBIn?.topicTags)
      ? sectionBIn.topicTags
      : Array.isArray(input?.topicTags)
      ? input.topicTags
      : Array.isArray(input?.tags)
      ? input.tags
      : [],
    content: safeString(sectionBIn?.content ?? input?.content, ""),
    attachments: Array.isArray(sectionBIn?.attachments) ? sectionBIn.attachments : [],
  };

  const riskScore = input?.riskScore != null ? clampInt(input?.riskScore, 50) : undefined;

  return {
    id,
    type,
    status,
    riskLevel,
    ...(riskScore != null ? { riskScore } : {}),
    agencyName: agencyName || undefined,
    agencyId: agencyId || undefined,
    ownerDept: ownerDeptTop || undefined,
    source: safeString(input?.source, ""),
    seedKey: safeString(input?.seedKey, ""),
    sectionA,
    sectionB,
    coAssistSections: Array.isArray(input?.coAssistSections) ? input.coAssistSections : [],
    approvalFlowId: safeString(input?.approvalFlowId, ""),
    approvalHistory: Array.isArray(input?.approvalHistory) ? input.approvalHistory : [],
    rejectionLog: Array.isArray(input?.rejectionLog) ? input.rejectionLog : [],
    createdAt: input?.createdAt ?? null,
    updatedAt: input?.updatedAt ?? null,
    archivedAt: input?.archivedAt ?? null,
    createdBy: safeString(input?.createdBy, "unknown"),
  } as IComplianceCard;
}

async function getCardById(id: string): Promise<IComplianceCard | null> {
  const snap = await getDoc(doc(db, CARDS_COLLECTION, id));
  if (!snap.exists()) return null;
  return normalizeComplianceCardForUI(id, snap.data());
}

export async function submitCardForApproval(
  cardOrId: IComplianceCard | string,
  user?: any
): Promise<void> {
  const id = typeof cardOrId === "string" ? cardOrId : safeString((cardOrId as any)?.id);
  if (!id) throw new Error("submitCardForApproval: missing card id");

  const card = typeof cardOrId === "string" ? await getCardById(id) : (cardOrId as any);
  const type: CardType = normalizeType((card as any)?.type);
  const approvalModule: ApprovalModule = type === "project" ? "GOV_PROJECT" : "OFFICIAL_REPLY";

  const uid = getUserUid(user);
  const name = getUserName(user);
  const ownerDept = safeString((card as any)?.sectionB?.ownerDept, "LG");

  // Create/reset flow first (so UI can open ApprovalFlowPanel immediately)
  await createOrResetApprovalFlow(approvalModule, id, uid, ownerDept, name);

  // Update card status + flow linkage
  await updateComplianceCard(id, {
    status: "IN_REVIEW" as any,
    approvalFlowId: `${approvalModule}_${id}` as any,
    submittedAt: new Date().toISOString(),
    submittedBy: uid,
  } as any);
}

export async function archiveCard(id: string, user?: any): Promise<void> {
  const uid = getUserUid(user);
  await updateComplianceCard(id, {
    status: "ACK_ARCHIVED" as any,
    archivedAt: new Date().toISOString(),
    archivedBy: uid,
  } as any);
}

export async function moveIntelToProject(
  cardOrId: IComplianceCard | string,
  user?: any
): Promise<void> {
  const id = typeof cardOrId === "string" ? cardOrId : safeString((cardOrId as any)?.id);
  if (!id) throw new Error("moveIntelToProject: missing card id");

  const uid = getUserUid(user);
  await updateComplianceCard(id, {
    type: "project" as any,
    status: "NOT_STARTED" as any,
    movedToProjectAt: new Date().toISOString(),
    movedToProjectBy: uid,
  } as any);
}

export async function deleteComplianceCard(id: string, _user?: any): Promise<void> {
  // Prefer server-side callable (records audit / enforces auth)
  try {
    const fn = (functions as any).httpsCallable?.("deleteComplianceCard");
    if (fn) {
      await fn({ cardId: id });
      return;
    }
  } catch (e) {
    // fallthrough
  }
  // Fallback direct delete (in case functions not deployed yet)
  await deleteDoc(doc(db, CARDS_COLLECTION, id));
}

// ----------------------------
// Crawler trigger
// ----------------------------

export async function runComplianceCrawlerNow(): Promise<{
  created: number;
  updated: number;
  dedupSkipped: number;
  runId: string;
}> {
  const fn = (functions as any).httpsCallable("runComplianceCrawlerNow");
  const res = await fn({});
  return res.data;
}

// ----------------------------
// Seed CSV -> Firestore cards
// ----------------------------

type SeedRow = {
  主管機關: string;
  發布日期: string;
  主旨: string;
  摘要: string;
  發布連結: string;
  附件下載連結: string;
  主責單位: string;
};

function coerceSeedRow(r: Record<string, string>): SeedRow {
  return {
    主管機關: r["主管機關"] ?? "",
    發布日期: r["發布日期"] ?? "",
    主旨: r["主旨"] ?? "",
    摘要: r["摘要"] ?? "",
    發布連結: r["發布連結"] ?? "",
    附件下載連結: r["附件下載連結"] ?? "",
    主責單位: r["主責單位"] ?? "",
  };
}

function splitLinks(raw: string): string[] {
  const s = safeString(raw);
  if (!s) return [];
  return s
    .split(/\s*\|\s*|\s*;\s*|\s*,\s*/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

export async function refreshSeedCards(): Promise<number> {
  const resp = await fetch("/seeds/company_obligations_ads_ecom_100.csv", {
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new Error(`Seed CSV 讀取失敗：${resp.status}`);
  }
  const text = await resp.text();
  const parsed = parseCsv(text).map(coerceSeedRow);

  let upserted = 0;
  for (const r of parsed) {
    // stable key
    const keyBase = `${r.主管機關}|${r.發布日期}|${r.主旨}`;
    const seedKey = await sha256Hex(keyBase);
    const docId = `seed_${seedKey.slice(0, 16)}`;

    const ref = doc(db, CARDS_COLLECTION, docId);
    const snap = await getDoc(ref);
    const now = serverTimestamp();

    const ownerDept = mapOwnerDept(r.主責單位);
    const attachmentLinks = splitLinks(r.附件下載連結);

    const payload: Record<string, any> = {
      type: "project" as CardType,
      status: "NOT_STARTED" as CardStatus,
      agencyName: safeString(r.主管機關),
      agencyId: "",
      ownerDept,
      riskScore: 50,
      riskLevel: "MEDIUM" as RiskLevel,
      source: "seed_csv:company_obligations_ads_ecom_100",
      seedKey,
      sectionA: {
        title: safeString(r.主旨),
        subject: safeString(r.主旨),
        date: safeString(r.發布日期) || null,
        dateText: safeString(r.發布日期),
        summary: safeString(r.摘要),
        sourceUrl: safeString(r.發布連結),
        announcementUrl: safeString(r.發布連結),
        attachmentLinks,
        attachments: toAttachmentMetaArray(attachmentLinks),
        legalBasis: [],
        tags: ["seed"],
      },
      sectionB: {
        ownerDept,
        ownerDeptSuggestionText: safeString(r.主責單位),
        content: "",
        attachments: [],
        topicTags: [],
      },
      coAssistSections: [],
      updatedAt: now,
    };
    if (!safeSnapExists(snap)) payload.createdAt = now;

    await setDoc(ref, payload, { merge: true });
    upserted++;
  }
  return upserted;
}

// Backward compatible alias used by existing UI
export async function seedInitialProjectCards(): Promise<{ created: number; updated: number }> {
  const count = await refreshSeedCards();
  // This seed uses stable docIds; treat all as updated for UI messaging
  return { created: 0, updated: count };
}

